import { DownloadOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { App, Button, Drawer, Flex, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { orDash } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import {
  exportPayableItemsCsv,
  exportReceivableItemsCsv,
  fetchPayableItems,
  fetchReceivableItems,
  type Ledger,
  type LedgerItem,
  type LedgerItemsFilter,
} from './api';

const { Text } = Typography;

export interface LedgerItemsDrawerProps {
  ledger: Ledger;
  /** Which items to list; null closes the drawer. */
  filter: LedgerItemsFilter | null;
  title: string;
  onClose: () => void;
}

const date = (value: string | null): string => (value === null ? '—' : dayjs(value).format('D MMM YYYY'));

/** The AutoCount documents behind one figure: a bucket, a customer or supplier, or the unmatched receipts, payments and credit notes. */
export function LedgerItemsDrawer({ ledger, filter, title, onClose }: LedgerItemsDrawerProps): ReactNode {
  const { t } = useTranslation();
  const copy = ledger === 'AR' ? 'receivables' : 'payables';
  const { message } = App.useApp();

  const { data, error, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['analytics', copy, 'items', filter],
    queryFn: ({ pageParam }) => (ledger === 'AR' ? fetchReceivableItems : fetchPayableItems)(filter as LedgerItemsFilter, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: filter !== null,
  });

  const rows = data?.pages.flatMap((page) => page.items) ?? [];

  const download = async (): Promise<void> => {
    if (filter === null) return;
    try {
      const csv = await (ledger === 'AR' ? exportReceivableItemsCsv : exportPayableItemsCsv)(filter);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${copy}-${filter.kind.toLowerCase()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      void message.error(t(`insights.${copy}.items.downloadFailed`));
    }
  };

  const columns: TableProps<LedgerItem>['columns'] = [
    {
      title: t(`insights.${copy}.items.document`),
      key: 'doc',
      render: (_: unknown, r) => <Text className="tabular">{`${r.kind} ${r.docNo}`}</Text>,
    },
    {
      title: t(`insights.${copy}.items.date`),
      key: 'date',
      render: (_: unknown, r) => <span className="tabular">{date(r.docDate)}</span>,
    },
    {
      title: t(`insights.${copy}.items.due`),
      key: 'due',
      render: (_: unknown, r) => <span className="tabular">{date(r.dueDate)}</span>,
    },
    ...(ledger === 'AP'
      ? [
          {
            title: t('insights.payables.items.supplierInvoiceNo'),
            key: 'supplierInvoiceNo',
            render: (_: unknown, r: LedgerItem) => <span className="tabular">{orDash(r.supplierInvoiceNo)}</span>,
          },
        ]
      : []),
    {
      title: t(`insights.${copy}.items.party`),
      key: 'party',
      render: (_: unknown, r) => (
        <Flex vertical>
          <span>{orDash(r.partyName)}</span>
          <Text type="secondary" className="tabular">
            {r.partyCode}
          </Text>
        </Flex>
      ),
    },
    {
      title: t(`insights.${copy}.items.amount`),
      key: 'amount',
      align: 'right',
      render: (_: unknown, r) => formatMoney(r.amount),
    },
    {
      title: t(`insights.${copy}.items.daysPastDue`),
      key: 'days',
      align: 'right',
      render: (_: unknown, r) => (r.daysPastDue === null ? '—' : r.daysPastDue),
    },
  ];

  return (
    <Drawer
      open={filter !== null}
      title={title}
      onClose={onClose}
      width={960}
      className="nks-action-drawer"
      extra={
        <Button
          icon={<DownloadOutlined />}
          onClick={() => {
            void download();
          }}
        >
          {t('insights.lines.downloadCsv')}
        </Button>
      }
    >
      <Flex vertical gap={12}>
        {error !== null && (
          <Text type="danger">
            {error instanceof ApiError ? error.message : t(`insights.${copy}.items.loadFailed`)}
          </Text>
        )}
        <Table<LedgerItem>
          rowKey="uid"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          loading={filter !== null && isPending}
          locale={{ emptyText: t(`insights.${copy}.items.empty`) }}
          dataSource={rows}
          columns={columns}
        />
        {hasNextPage && (
          <Button
            block
            loading={isFetchingNextPage}
            onClick={() => {
              void fetchNextPage();
            }}
          >
            {t(`insights.${copy}.items.loadMore`)}
          </Button>
        )}
      </Flex>
    </Drawer>
  );
}
